import { useState, useEffect, useCallback } from "react";
import CommonTitle from "../../ui-components/CommonTitle";
import DatakanriDataMakingLocation from "../../ui-components/DatakanriDataMakingLocation";
import { Flex, TextField, Text } from "@aws-amplify/ui-react";
import CommonBtnBlue from "../../ui-components/CommonBtnBlue";
import { DEFAULT_DISPLAY_SETTING, locationKeyList } from "../../lib/ConstList";
import { API } from 'aws-amplify';
import {
  getDisplaySettingJson,
} from '../../lib/SettingUtil';
import { getFormatNowDate, downloadBlob } from '../../lib/StorageUtil';
import TraceForPlottingSelecter from "./TraceForPlottingSelecter";
import { LoadingWrapper } from "../../componentsWrapper/LoadingWrapper";

const PlotRegenerateModal = ({
  onClose,
}) => {
  // 表示ラベル設定
  const [displaySettingJson, setDisplaySettingJson] = useState(DEFAULT_DISPLAY_SETTING);

  // 選択された地点
  const [selectedLocation, setSelectedLocation] = useState(locationKeyList[0]);

  // トレースファイル
  const [selectedTraceFile, setSelectedTraceFile] = useState("");

  // スケール
  const [scale, setScale] = useState({
    min: "-145",
    max: "-95",
  });

  // ロード中フラグ
  const [isLoading, setIsLoading] = useState(false);

  // エラーメッセージ
  const [errors, setErrors] = useState({
    scaleMin: "",
    scaleMax: "",
  });

  // バリデーション関数
  const validateInputs = () => {
    const newErrors = {
      scaleMin: "",
      scaleMax: "",
    };

    console.warn("scale", scale);
    

    try {
      // スケール最小値のチェック
      const minValue = parseInt(scale.min);
      console.warn("minValue", minValue);
      
      if (isNaN(minValue)) {
        newErrors.scaleMin = "最小値を入力してください";
        throw new Error(newErrors.scaleMin);
      }

      // スケール最大値のチェック
      const maxValue = parseInt(scale.max);
      if (isNaN(maxValue)) {
        newErrors.scaleMax = "最大値を入力してください";
        throw new Error(newErrors.scaleMax);
      }

      // スケール範囲の論理チェック
      if (!isNaN(minValue) && !isNaN(maxValue)) {
        if (minValue >= maxValue) {
          newErrors.scaleMin = "最小値は最大値より小さくしてください";
          throw new Error(newErrors.scaleMin);
        }
      }
    } catch (e) {
      console.warn(e);
    }

    setErrors(newErrors);

    // エラーがあるかチェック
    return !Object.values(newErrors).some(error => error !== "");
  };

  // 表示設定取得
  const initDisplaySettingJson = useCallback(async () => {
    const setting = await getDisplaySettingJson();
    if (setting !== null) {
      setDisplaySettingJson(setting);
    } else {
      console.log("displaySettingJson is null");
    }
  }, []);

  // 作成ボタンクリック時の処理
  const onClickReCreateBtn = async () => {
    // バリデーション実行
    if (!validateInputs()) {
      return;
    }

    // ロード中フラグを立てる
    setIsLoading(true);

    try {
      const res = await API.post("5gApi", "plot-regeneration", {
        body: {
          location: selectedLocation,
          traceFile: "public/"+selectedTraceFile,
          scale: {
            min: Number(scale.min),
            max: Number(scale.max),
          },
        }
      })

      // base64データをデコード
      const binaryRes = Buffer.from(res.imageData, 'base64');
      const blob = new Blob([binaryRes.buffer], { type: 'image/png' })

      // ファイル名はヘッダーから取得
      const filename = res.filename;
      console.log(filename);

      // ダウンロード
      downloadBlob(blob, filename);
    } catch (e) {
      console.log(e);
    }

    // モーダルを閉じる
    onClose();

    // ロード中フラグを下げる
    setIsLoading(false);
  }

  useEffect(() => {
    // 表示設定取得
    initDisplaySettingJson();
  }, [initDisplaySettingJson]);

  return (
    <>
      <Flex
        gap="56px"
        direction="column"
        alignItems="center"
        borderRadius="20px"
        padding="40px"
      >
        <Flex
          gap="40px"
          direction="column"
          alignItems="center"
          shrink="0"
        >
          {/* タイトル */}
          <CommonTitle
            shrink="0"
            alignSelf="stretch"
            position="relative"
            size="large"
            overrides={{
              text: {
                children: "カスタムプロット再生成",
              }
            }}
          ></CommonTitle>

          {/* モーダルの内容 */}
          <Flex
            gap="32px"
            direction="column"
            alignItems="center"
            shrink="0"
          >
            {/* 拠点選択 */}
            <DatakanriDataMakingLocation
              error="false"
              example="false"
              width="100%"
              overrides={{
                SelectField: {
                  width: "100%",
                  value: selectedLocation,
                  placeholder: undefined,
                  children: (
                    <>
                      {
                        Object.keys(displaySettingJson.location_name).map((key, index) => {
                          return (
                            <option
                              key={key}
                              value={key}
                            >
                              {displaySettingJson.location_name[key]}
                            </option>
                          )
                        })
                      }
                    </>
                  ),
                  onChange: (e) => {
                    console.log(e.target.value);
                    setSelectedLocation(e.target.value);
                  }
                },
              }}
            />

            {/* 日時 */}
            <TraceForPlottingSelecter
              location={selectedLocation}
              onChange={(traceFileKey) => {
                setSelectedTraceFile(traceFileKey);
              }}
            />

            {/* 縦スケール */}
            <Flex width="100%" direction="column">
              <Flex width="100%" alignItems="flex-end" gap="6px">
                <TextField
                  label="縦スケール [dBm/MHz]"
                  type="number"
                  width="100%"
                  min="-200"
                  max="0"
                  value={scale.min}
                  onChange={(e) => {
                    setScale({
                      ...scale,
                      min: e.target.value,
                    });
                    // エラーメッセージをクリア
                    setErrors(prev => ({ 
                      ...prev, 
                      scaleMin: "",
                      scaleMax: "" 
                    }));
                  }}
                />
                <Flex paddingBottom="7px">~</Flex>

                <TextField
                  type="number"
                  width="100%"
                  min="-200"
                  max="0"
                  value={scale.max}
                  onChange={(e) => {
                    setScale({
                      ...scale,
                      max: e.target.value,
                    });
                    // エラーメッセージをクリア
                    setErrors(prev => ({ 
                      ...prev, 
                      scaleMax: "",
                      scaleMin: "" 
                    }));
                  }}
                />
              </Flex>
              {/* スケールエラーメッセージ */}
              {(errors.scaleMin || errors.scaleMax) && (
                <Text color="red" fontSize="14px" marginTop="4px">
                  {errors.scaleMin || errors.scaleMax}
                </Text>
              )}
            </Flex>
          </Flex>
        </Flex>

        {/* 再生成ボタン */}
        <CommonBtnBlue
          size="large"
          style={{
            cursor: "pointer",
            opacity: isLoading ? 0.5 : 1,
          }}
          disabled={isLoading}
          overrides={{
            text: {
              children: (
                <>
                  {
                    isLoading ? (
                      <>
                        <LoadingWrapper />
                      </>
                    ) : "再生成"
                  }
                </>
              ),
              onClick: onClickReCreateBtn,
            }
          }}
        ></CommonBtnBlue>
      </Flex >
    </>
  );
}

export default PlotRegenerateModal;